import { useMutation } from '@apollo/client'
import { FormikHelpers, useFormik } from 'formik'
import { FC, useMemo } from 'react'
import * as Yup from 'yup'

import { Button, Input, StarSelector } from '@syconium/little-miss-figgy'

import { reportClientError } from '../../../../../app/_components/chrome/scripts/DataDogRumScript'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { CREATE_PRODUCT_REVIEW } from '../../../../../lib/graphql/mutations/CREATE_PRODUCT_REVIEW'

import {
  ErrorMessage,
  Form,
  FormErrorMessage,
  InputLabel,
  PostCtaWrapper,
  ReviewBodyTextAreaWrapper,
  StarSelectorWrapper,
  TextInputGrid,
} from './styles'

type ReviewFormProps = {
  handle: string
  hideAndDisableReviewForm: () => void
}

type ReviewFormValues = {
  body: string
  email: string
  name: string
  occupation: string
  starRating: number
  subject: string
}

export const ReviewForm: FC<ReviewFormProps> = ({ handle, hideAndDisableReviewForm }) => {
  const {
    magnolia: {
      reviewsPage: {
        reviewForm: { formSubmissionErrorMessage, validationErrorMessages, inputLabels, submitCta },
      },
    },
  } = useTranslation()

  const [createProductReview] = useMutation(CREATE_PRODUCT_REVIEW)

  const onSubmit = async (values: ReviewFormValues, actions: FormikHelpers<ReviewFormValues>) => {
    try {
      // NOTE: this endpoint currently times out after ~15 seconds.
      // The response will just be a failure without status code.
      // Future backend changes will support an async queue implementation,
      // so we aren't making changes to handle the timeout at this time.
      const { errors } = await createProductReview({
        variables: {
          review: {
            content: values.body,
            domain: window.location.origin,
            email: values.email,
            handle,
            name: values.name,
            path: window.location.pathname,
            score: values.starRating.toString(),
            title: values.subject,
          },
        },
      })

      // Throw if the GraphQL response returns any errors
      if (errors?.length) {
        throw errors
      }

      // If no error, reset form status & hide the review form
      actions.setStatus(undefined)
      hideAndDisableReviewForm()
    } catch (error) {
      reportClientError({
        error,
        context: {
          scope: 'Write new review',
          label: 'mutation failed',
        },
      })

      // Set the form-level error message
      actions.setStatus(formSubmissionErrorMessage)
    }
  }

  const {
    handleSubmit,
    handleBlur,
    // Form status will be set to a form-level error message if review creation fails
    status: formErrorMessage,
    handleChange,
    values,
    errors,
    touched,
    setFieldValue,
  } = useFormik<ReviewFormValues>({
    initialValues: {
      email: '',
      name: '',
      occupation: '',
      body: '',
      starRating: 0,
      subject: '',
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .required(validationErrorMessages.requiredText)
        .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, validationErrorMessages.validEmail),
      name: Yup.string().required(validationErrorMessages.requiredText),
      occupation: Yup.string(),
      body: Yup.string().required(validationErrorMessages.requiredText),
      starRating: Yup.number().test(
        'is-valid-star-rating',
        validationErrorMessages.requiredStarRating,
        (val: number | undefined) => {
          return val !== 0 && val !== undefined
        }
      ),
      subject: Yup.string().required(validationErrorMessages.requiredText),
    }),
    onSubmit,
  })

  // Only show errors for fields that have been touched (i.e. blurred, or submit attempted)
  const visibleErrors = useMemo(() => {
    return Object.entries(errors).reduce<{ [key in keyof ReviewFormValues]?: string }>(
      (acc, [fieldKey, error]) => {
        if (touched[fieldKey as keyof ReviewFormValues]) {
          acc[fieldKey as keyof ReviewFormValues] = error
        }
        return acc
      },
      {}
    )
  }, [errors, touched])

  return (
    <Form noValidate onSubmit={handleSubmit} aria-label='Submit a review'>
      <StarSelectorWrapper>
        <InputLabel id='star-selector-label'>{inputLabels.starRating}</InputLabel>
        <StarSelector
          ariaLabelledBy='star-selector-label'
          onStarClick={rating => setFieldValue('starRating', rating)}
        />
        {visibleErrors.starRating && <ErrorMessage>{visibleErrors.starRating}</ErrorMessage>}
      </StarSelectorWrapper>

      <TextInputGrid>
        <label>
          <InputLabel required>{inputLabels.name}</InputLabel>
          <Input
            id='name'
            name='name'
            onBlur={handleBlur}
            onChange={handleChange}
            isInvalid={!!visibleErrors.name}
            messageWhenInvalid={visibleErrors.name}
            type='text'
            value={values.name}
          />
        </label>

        <label>
          <InputLabel required>{inputLabels.subject}</InputLabel>
          <Input
            id='subject'
            name='subject'
            onBlur={handleBlur}
            onChange={handleChange}
            isInvalid={!!visibleErrors.subject}
            messageWhenInvalid={visibleErrors.subject}
            type='text'
            value={values.subject}
          />
        </label>

        <label>
          <InputLabel required>{inputLabels.email}</InputLabel>
          <Input
            id='email'
            name='email'
            onBlur={handleBlur}
            onChange={handleChange}
            isInvalid={!!visibleErrors.email}
            messageWhenInvalid={visibleErrors.email}
            type='email'
            value={values.email}
          />
        </label>

        <label>
          <InputLabel>{inputLabels.occupation}</InputLabel>
          <Input
            id='occupation'
            name='occupation'
            onBlur={handleBlur}
            onChange={handleChange}
            isInvalid={!!visibleErrors.occupation}
            messageWhenInvalid={visibleErrors.occupation}
            type='text'
            value={values.occupation}
          />
        </label>

        <ReviewBodyTextAreaWrapper>
          <InputLabel required>{inputLabels.reviewBody}</InputLabel>
          <Input
            id='body'
            name='body'
            onBlur={handleBlur}
            onChange={handleChange}
            isInvalid={!!visibleErrors.body}
            messageWhenInvalid={visibleErrors.body}
            kind='textarea'
            value={values.body}
          />
        </ReviewBodyTextAreaWrapper>
      </TextInputGrid>

      {formErrorMessage && <FormErrorMessage>{formErrorMessage}</FormErrorMessage>}

      <PostCtaWrapper>
        <Button type='submit'>{submitCta}</Button>
      </PostCtaWrapper>
    </Form>
  )
}
